/**
 * Internal domain event names (D-13).
 *
 * The hub emits these typed domain events through an internal emitter that the WS
 * fan-out layer consumes. A future server-side LLM consumer subscribes to the same bus —
 * no new transport is built for it (the RT-06 server-consumable event/context stream seam).
 *
 * Phase 2 additions: the portal subscribes to `CONVERSATION_CREATED` to live-merge new
 * conversations into the inbox (D-09), and the presence event names (`PRESENCE_SET`,
 * `PRESENCE_ACK`, `PRESENCE_HEARTBEAT`) carry the agent online/away state machine the
 * hub emits and the portal subscribes to (D-10/D-12). These names are the single source
 * of truth on the wire — both the hub and the portal import the identical compiled strings.
 *
 * Phase 3 additions: `SESSION_CREATE` is the INBOUND WS event from the customer client
 * (client → hub). This is DISTINCT from `SESSION_CREATED` which is the OUTBOUND domain
 * event emitted by the hub on its internal bus (hub internal bus only).
 * - `session.create` (inbound): customer widget → hub CustomerGateway
 * - `session.created` (outbound): hub SessionService → internal domain event bus
 *
 * Phase 4 additions: new INBOUND client→gateway chat events and one new OUTBOUND
 * fanout event, distinct from the existing outbound `MESSAGE_CREATED`. The
 * `message.send` handlers persist via `ChatService.persist()` and let the existing
 * `FanoutSubscriber` emit `'message'` (MESSAGE_CREATED's wire payload) — these new
 * inbound names are the client→hub triggers, not new outbound payload shapes:
 * - `message.send` (inbound): customer widget / agent portal → hub gateway — triggers
 *   `ChatService.persist()`, which auto-reopens a CLOSED conversation (D-42) and lets
 *   the existing FanoutSubscriber broadcast `'message'` (MESSAGE_CREATED).
 * - `typing.start` / `typing.stop` (inbound, transient relay): relayed directly to the
 *   conversation room — NEVER persisted, NEVER routed through ChatService.persist().
 * - `message.read` (inbound + outbound fanout): inbound flips `MessageEntity.isRead`
 *   and the hub fans the same event name back out to the conversation room.
 * - `conversation.status-changed` (outbound only, D-45): emitted to the AGENTS_ROOM on
 *   the /agent namespace ONLY when a CLOSED conversation auto-reopens via `message.send`
 *   — never to the /customer namespace, never a bare `server.emit`.
 *
 * INVARIANT: identity (participant/authorType/tenantSlug) for every Phase 4 inbound
 * event is derived from the handshake-bound `client.data`, NEVER from the event payload.
 * No handler may trust a participant/sender identity carried in the payload.
 */
export const SUPPORT_EVENTS = {
    MESSAGE_CREATED: 'message.created',
    PARTICIPANT_JOINED: 'participant.joined',
    SESSION_CREATED: 'session.created',
    CONTEXT_ATTACHED: 'context.attached',
    // Phase 2 additions — portal subscribes, hub emits.
    CONVERSATION_CREATED: 'conversation.created',
    PRESENCE_SET: 'presence.set',
    PRESENCE_ACK: 'presence.ack',
    PRESENCE_HEARTBEAT: 'presence.heartbeat',
    /**
     * Inbound WS event: customer widget → hub CustomerGateway (Phase 3).
     * Carries the context + log bundle in a `SessionCreatePayload`.
     * DISTINCT from `SESSION_CREATED` ('session.created') which is the outbound hub domain event.
     */
    SESSION_CREATE: 'session.create',
    /**
     * Phase 4 — inbound: customer widget / agent portal → hub gateway. Triggers
     * `ChatService.persist()`; the existing FanoutSubscriber's `MESSAGE_CREATED`
     * broadcast ('message') is the delivery path — this handler does NOT emit directly.
     */
    MESSAGE_SEND: 'message.send',
    /** Phase 4 — inbound, transient relay to the conversation room. NEVER persisted. */
    TYPING_START: 'typing.start',
    /** Phase 4 — inbound, transient relay to the conversation room. NEVER persisted. */
    TYPING_STOP: 'typing.stop',
    /** Phase 4 — inbound + outbound fanout: flips `MessageEntity.isRead` and re-broadcasts. */
    MESSAGE_READ: 'message.read',
    /**
     * Phase 4 — outbound only (D-45): emitted to AGENTS_ROOM on the /agent namespace when
     * a CLOSED conversation auto-reopens via `message.send`. Never sent to /customer.
     */
    STATUS_CHANGED: 'conversation.status-changed',
} as const;

export type SupportEvent = (typeof SUPPORT_EVENTS)[keyof typeof SUPPORT_EVENTS];
